import type { PersonaTargetChoice, PersonaTargetRead, TargetAction } from '@/services/portability'
import {
  MATCHES_ARCHIVE,
  NO_CANDIDATES,
  SAME_NAME,
  TARGET_GROUP_LABEL,
  TARGET_LABELS,
  targetSelectLabel,
  updateLine,
} from './portabilityText'

const ORDER: TargetAction[] = ['create', 'update', 'skip']

interface Props {
  naturalKey: string
  target: PersonaTargetRead
  choice: PersonaTargetChoice
  onChange: (choice: PersonaTargetChoice) => void
}

/** The persona to preselect when the person turns to Update: the identity match, else the first served. */
function firstTarget(target: PersonaTargetRead): string | null {
  const match = target.identity_match?.id
  if (match && target.candidates.some((c) => c.id === match)) return match
  return target.candidates[0]?.id ?? null
}

export function PersonaTargetControl({ naturalKey, target, choice, onChange }: Props) {
  const picked = target.candidates.find((c) => c.id === choice.persona_id)
  const updateName = picked?.name ?? target.identity_match?.name ?? null
  const matchId = target.identity_match?.id
  // Update needs a persona to update; never offer it (and re-preview with no id) when there is none.
  const noCandidates = target.candidates.length === 0
  const shown = ORDER.filter(
    (a) => target.allowed.includes(a) && !(a === 'update' && noCandidates),
  )

  return (
    <div
      className="space-y-2 pl-4 text-sm"
      role="group"
      aria-label={`${TARGET_GROUP_LABEL}: ${target.name || naturalKey}`}
    >
      <div className="flex flex-wrap gap-4 text-foreground">
        {shown.map((action) => (
          <label key={action} className="inline-flex items-center gap-2">
            <input
              type="radio"
              name={`persona-target-${naturalKey}`}
              checked={choice.action === action}
              onChange={() =>
                onChange({
                  action,
                  persona_id: action === 'update' ? firstTarget(target) : null,
                })
              }
            />
            {TARGET_LABELS[action]}
          </label>
        ))}
      </div>
      {noCandidates && target.allowed.includes('update') && choice.action !== 'update' && (
        <p className="text-muted-foreground">{NO_CANDIDATES}</p>
      )}
      {choice.action === 'update' && (
        <div className="space-y-1">
          {noCandidates ? (
            <p className="text-muted-foreground">{NO_CANDIDATES}</p>
          ) : (
            <select
              aria-label={targetSelectLabel(naturalKey)}
              value={choice.persona_id ?? ''}
              onChange={(e) => onChange({ action: 'update', persona_id: e.target.value || null })}
              className="w-80 rounded-lg border border-border bg-card px-3 py-1.5 text-sm text-foreground"
            >
              {target.candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {[
                    c.name,
                    c.same_name ? SAME_NAME : null,
                    c.id === matchId ? MATCHES_ARCHIVE : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </option>
              ))}
            </select>
          )}
          {updateName && <p className="text-muted-foreground">{updateLine(updateName)}</p>}
        </div>
      )}
    </div>
  )
}
